import * as React from "react";

import { Card, Divider, makeStyles } from "@material-ui/core";

import "react-perfect-scrollbar/dist/css/styles.css";

import { PageHeader, BackButton } from "jazasoft";

import Table from "../../../components/Table";
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";

import { getDistinctValues, isEmpty } from "../../../utils/helpers";
import { useDeepMemo, useQuery } from "../../../hooks";

const NON_SB_FIELDS = ["label"];

const useStyles = makeStyles({
  content: {
    margin: "0 1.5em",
    marginBottom: "2em"
  },
  mrTop: {
    marginTop: "1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  }
});

const SizeChartView = props => {
  const { id } = props;
  const classes = useStyles();

  const { data: sizeChart = {} } = useQuery({ url: `size-chart/${id}` });

  const infoColumns = useDeepMemo(() => {
    const columns = [
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "productName", title: "Product" },
      { dataKey: "style", title: "Style", render: value => value || "-" },
      { dataKey: "fit", title: "Fit", render: value => value || "-" },
      { dataKey: "fabricWidth", title: "Cuttable Width", render: value => value || "-" },
      { dataKey: "fabricPatternName", title: "Fabric Pattern", render: value => value || "-" },
      { dataKey: "repeat", title: "Repeat", render: value => value || "-" },
      { dataKey: "fabricDirection", title: "Fabric Direction", render: value => value || "-" },
      { dataKey: "csv", title: "CSV", render: value => value || "-" }
    ];
    return columns;
  }, []);

  const infoRows = useDeepMemo(() => {
    return [
      {
        ...sizeChart,
        customerName: sizeChart?.customer?.name,
        productName: sizeChart?.product?.name,
        fabricPatternName: sizeChart?.fabricPattern?.name
      }
    ];
  }, [sizeChart]);

  //** Prepare Size Breakup Columns for View mode **/
  const { columns, headerRows } = useDeepMemo(() => {
    if (isEmpty(sizeChart)) return { columns: [], headerRows: [] };

    const sizeList = sizeChart?.sizeList?.sort((a, b) => a.serialNo - b.serialNo) || [];

    const haveSizeGroup = getDistinctValues(sizeList.map(size => size.sizeGroup))?.length > 1;

    const sizeKeys = sizeList.map(e => e.sizeKey);

    let groupRow = {};
    let sizes = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sizeList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizes.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        element: ({ record, column }) => <span>{record[column.dataKey] || "-"}</span>,
        leftBorder
      });
    }

    let columns = [...sizes];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) => {
        const cSpan =
          !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
            ? 1
            : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length;
        return cSpan;
      }
    }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }
    return { columns, headerRows };
  }, [sizeChart]);

  //** Prepare Size Breakup Rows for View mode **/
  const rows = useDeepMemo(() => {
    if (isEmpty(sizeChart)) return [];

    const sizeList = sizeChart?.sizeList?.sort((a, b) => a.serialNo - b.serialNo) || [];
    const row = sizeList.reduce((acc, e) => ({ ...acc, [e.sizeKey]: e.consumption }), {});
    return [row];
  }, [sizeChart]);

  return (
    <div>
      <PageHeader title="Size Chart" />

      <div className={classes.content}>
        <MuiTable title={<CardHeader title="Basic Info" style={{ padding: 0 }} />} toolbar columns={infoColumns} rows={infoRows} />

        <Card className={classes.mrTop}>
          <CardHeader title="Size Chart" />
          <Divider />
          <Table
            columns={columns}
            headerRows={headerRows}
            rows={rows}
            headerCellStyle={column => ({
              width: NON_SB_FIELDS.includes(column?.dataKey) ? 150 : undefined,
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              fontWeight: "bold",
              backgroundColor: "#C6D0DA"
            })}
            cellStyle={({ column, record }) => ({
              fontWeight: record.isTotalRow ? "bold" : "normal",
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
            })}
          />
        </Card>

        <div style={{ padding: "2.5em 0" }}>
          <BackButton variant="outlined" color="primary" />
        </div>
      </div>
    </div>
  );
};

export default SizeChartView;
